import {KTSVG} from '../../helpers'
import React, {memo, useCallback, useMemo, useRef, useState} from 'react'
import {FormikHelpers, useFormik} from 'formik'
import * as Yup from 'yup'
import {useStatusMessage} from '../../../app/features'
import {initialValuesCreateTicket, initialValuesCreateTicketType} from './types'
import {FileInput, Select, SelectOption, TextArea, TextInput} from '../content/forms'
import {useQuery} from 'react-query'
import {Get, Post} from '../../../setup'
import {useSAV, SAVAssign, SAVProductAssignResponse} from '../../../app/@types'
import {useNotifications} from '../../../app/@contexts'
import {Modal} from './Modal'
import {SubmitButton} from '../../../app/@components'

const TICKETS_FORM = process.env.REACT_APP_SAV_TICKETS_FORM || 'sav/data-ticket'
const TICKETS_CREATE = process.env.REACT_APP_SAV_TICKETS_CREATE || 'sav/save-ticket'
const SPECIAL_PRODUCT_IDS = [6, 7]

const requiresAssignSelection = (productId: number) =>
    !isNaN(productId) && !SPECIAL_PRODUCT_IDS.includes(productId)

const Schema = Yup.object({
    subject: Yup.string()
        .min(3, 'Minimum 3 symbols')
        .max(100, 'Maximum 100 symbols')
        .required('Obligatoire'),
    products: Yup.string()
        .required('Obligatoire')
        .nullable(),

    description: Yup.string()
        .required('Obligatoire'),
    files: Yup.mixed(),
    assigns: Yup.string().when('products', {
        is: (val: string) => requiresAssignSelection(Number(val)),
        then: Yup.string().required('Obligatoire'),
    }),
    // assigns: Yup.string().when('products', {
    //     is: (val: string) => val !== '7' && val !== '6',
    //     then: Yup.string()
    //         .required('Obligatoire'),
    // }),
})

const FormHeader = memo(() => (
    <div className="modal-header">
        <h5>Créer un ticket</h5>
        <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
            <KTSVG path="/media/icons/duotune/arrows/arr061.svg" className="svg-icon-1" />
        </div>
    </div>
))

const CreateTicketSav = () => {

    const {triggerReload} = useSAV()
    const closeRef = useRef<HTMLButtonElement | null>(null)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const {formState, setLoading} = useStatusMessage()
    const {setToast} = useNotifications()

    const [filesCount, setFilesCount] = useState<number>(0)

    const onSubmit = async (values: initialValuesCreateTicketType, {
        setSubmitting,
        resetForm,
    }: FormikHelpers<initialValuesCreateTicketType>) => {

        setLoading(true)
        setSubmitting(false)

        try {

            const formData = new FormData()
            formData.append('subject', values.subject)
            formData.append('product', values.products)
            formData.append('description', values.description)
            values.files.forEach(file => formData.append('files[]', file))
            if (values.assigns && requiresAssignSelection(Number(values.products))) {
                formData.append('assign', values.assigns)
            }

            const {status, message} = await Post<{status: number, message: string}>(TICKETS_CREATE, formData)

            if (status === 200) {
                setToast({type: 'success', text: {title: 'Ticket', message}})
            }

            await Promise.all([
                triggerReload(),
                new Promise(resolve => setTimeout(resolve, 2500))
            ])

            resetForm()
            resetTicketForm()
            handleModalClose()
        } catch (e) {
            console.log(e)
            setToast({
                type: 'error',
                text: {
                    title: 'Ticket',
                    message: 'Échec de la création de ticket'
                }
            })
        } finally {
            setLoading(false)
            setSubmitting(false)
        }
    }

    const formik = useFormik({
        initialValues: initialValuesCreateTicket,
        validationSchema: Schema,
        enableReinitialize: true,
        onSubmit,
    })

    const {data: ticketFormData} = useQuery(
        'TICKETS_PRODUCT_ASSIGN',
        () => Get<SAVProductAssignResponse>({endpoint: TICKETS_FORM}),
        {
            staleTime: 5 * 60 * 1000, // Cache for 5 minutes
            cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
        },
    )

    const selectedProductId = Number(formik.values.products)
    const showAssignsSelect = requiresAssignSelection(selectedProductId)

    const products = useMemo(
        () => ticketFormData?.status === 200 ? ticketFormData.products : [],
        [ticketFormData],
    )

    const assigns = useMemo(
        () => ticketFormData?.status === 200 ? ticketFormData.assigns : [],
        [ticketFormData],
    )

    const productsOptions = useMemo<SelectOption[]>(
        () => [
            {value: '', label: ''},
            ...products.map((product) => ({
                value: product.id.toString(),
                label: product.name,
            })),
        ],
        [products],
    )

    const assignsByProduct = useMemo(
        () =>
            assigns.reduce<Record<string, SAVAssign[]>>((accumulator, assign) => {
                const key = String(assign.product_id)
                accumulator[key] = [...(accumulator[key] ?? []), assign]
                return accumulator
            }, {}),
        [assigns],
    )

    const assignsOptions = useMemo<SelectOption[]>(
        () => [
            {value: '', label: ''},
            ...(assignsByProduct[String(selectedProductId)] ?? []).map((assign) => ({
                value: assign.id.toString(),
                label: assign.name,
            })),
        ],
        [assignsByProduct, selectedProductId],
    )

    const resetTicketForm = useCallback(() => {
        setFilesCount(0)
        if (fileInputRef.current) {
            fileInputRef.current.value = ''
        }
    }, [])

    const handleSelectProductsChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
        formik.setFieldValue('assigns', '')
        formik.setFieldTouched('assigns', false)
    }, [formik])

    const handleModalClose = useCallback(() => {
        closeRef.current?.click()
    }, [])

    const formContent = useMemo(() => (
        <div className="row">
            <TextInput
                flex="column"
                formik={formik}
                name="subject"
                label="Sujet"
            />

            <div className="row my-2 my-lg-6">
                <div className="col-12 col-sm-6">
                    <div className="row d-flex flex-column">
                        <div className="col-3">
                            <label
                                htmlFor="products"
                                className="col-3 d-flex align-items-center col-form-label fw-bold fs-7"
                            >
                                Services
                            </label>
                        </div>
                        <div className="col-12">
                            <Select
                                options={productsOptions}
                                formik={formik}
                                name="products"
                                onChange={handleSelectProductsChange}
                            />
                        </div>
                    </div>
                </div>

                {showAssignsSelect && (
                    <div className="col-12 col-sm-6">
                        <div className="row d-flex flex-column">
                            <div className="col-3">
                                <label
                                    htmlFor="assigns"
                                    className="col-3 d-flex align-items-center col-form-label fw-bold fs-7"
                                >
                                    Détails
                                </label>
                            </div>
                            <div className="col-12">
                                <Select
                                    options={assignsOptions}
                                    formik={formik}
                                    name="assigns"
                                />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <TextArea
                cols={10}
                rows={5}
                flex="column"
                formik={formik}
                name="description"
                label="Description"
            />

            <FileInput
                fileInputRef={fileInputRef}
                filesCount={filesCount}
                setFilesCount={setFilesCount}
                formik={formik}
                description=".PNG, .JPG, .JPEG, .PDF, .DOCX, .CSV"
                accept=".png, .jpg, .jpeg, .pdf, .docx, .csv"
                name="file"
                multiple
            />
        </div>
    ), [
        formik,
        productsOptions,
        assignsOptions,
        showAssignsSelect,
        filesCount,
        handleSelectProductsChange
    ])

    return (
        <>
            <Modal
                button={{
                    bg: 'primary',
                    tooltip: 'Créer',
                    text: 'Créer',
                    icon: {show: true, svg: 'abstract/abs011'},
                }}
                modal={{
                    id: `create_ticket`,
                    size: 'lg',
                    content:
                        <form onSubmit={formik.handleSubmit}>

                            <FormHeader />
                            <div className="modal-body p-3 p-sm-5 p-md-7">
                                {formContent}
                            </div>
                            <div className="modal-footer">
                                <SubmitButton isSubmitting={formik.isSubmitting} isValid={formik.isValid}
                                              loading={formState.loading} label={'Envoyer'} />
                                <button type="button"
                                        className="btn btn-sm btn-light me-2 px-6"
                                        data-bs-dismiss="modal"
                                        onClick={() => {
                                            formik.resetForm()
                                            resetTicketForm()
                                        }}
                                        ref={closeRef}
                                >
                                    Fermer
                                </button>
                            </div>
                        </form>
                }}
            />
        </>
    )
}

export {CreateTicketSav}

